import React, { useEffect, useState } from 'react';
import { Button, Space, Typography } from 'antd';
import { RocketOutlined, CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useRole } from '../hooks/useRole';

const { Text } = Typography;

const DISMISSED_KEY = 'trial_banner_dismissed';

const TrialBanner: React.FC = () => {
  const { isTrial, trialDaysRemaining, isExpired, subscriptionStatus } = useAppContext();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISSED_KEY) === '1');

  // Reset dismissal on new session (sessionStorage clears automatically)
  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISSED_KEY) === '1');
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  // Show banner only when on trial and not dismissed
  if (!isTrial || dismissed) return null;

  const isExpiringSoon = (trialDaysRemaining ?? 99) <= 3;
  const bg = isExpired
    ? 'linear-gradient(90deg, #7f1d1d 0%, #991b1b 100%)'
    : isExpiringSoon
    ? 'linear-gradient(90deg, #92400e 0%, #b45309 100%)'
    : 'linear-gradient(90deg, #065f46 0%, #047857 100%)';

  const label = isExpired
    ? 'Your trial has expired.'
    : trialDaysRemaining === 0
    ? 'Your trial expires today.'
    : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining in your trial.`;

  return (
    <div
      style={{
        background: bg,
        padding: '7px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        flexShrink: 0,
      }}
    >
      <Space size={8} wrap>
        <ClockCircleOutlined style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }} />
        <Text style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>
          {label}
        </Text>
        {subscriptionStatus && (
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
            Status: {subscriptionStatus}
          </Text>
        )}
      </Space>

      <Space size={8}>
        {isAdmin && (
          <Button
            size="small"
            icon={<RocketOutlined />}
            onClick={() => navigate('/v1/admin/subscription')}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 6,
              backdropFilter: 'blur(4px)',
            }}
          >
            Upgrade Now
          </Button>
        )}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={handleDismiss}
          style={{ color: 'rgba(255,255,255,0.7)', padding: '0 4px' }}
        />
      </Space>
    </div>
  );
};

export default TrialBanner;
